/**
 * Admin user management.
 *  - Invite users (email magic-link)
 *  - List users (role, status, invited-by)
 *  - Resend invite to pending users
 *  - Delete users
 */
import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import {
  UserPlus, Trash, PaperPlaneTilt, ShieldCheck, User as UserIcon,
  Users as UsersIcon, CheckCircle, Hourglass,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { v: "admin",      l: "Admin",       desc: "Full access" },
  { v: "user",       l: "Technician",  desc: "Read/write, can't manage users" },
  { v: "client_poc", l: "Client POC",  desc: "Read-only, scoped to one client" },
];

const EMPTY = { email: "", name: "", role: "user", client_id: "" };

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/admin/users"),
        api.get("/clients"),
      ]);
      setUsers(u.data);
      setClients(c.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const invite = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        client_id: form.role === "client_poc" ? (form.client_id || null) : null,
      };
      await api.post("/admin/users/invite", payload);
      setShowInvite(false);
      setForm(EMPTY);
      flash(`Invite sent to ${payload.email}`);
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const resend = async (u) => {
    if (!window.confirm(`Resend invite to ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      flash(`Invite resent to ${u.email}`);
    } catch (e) {
      flash(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      flash(`Deleted ${u.email}`);
      load();
    } catch (e) {
      flash(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || id;

  return (
    <Layout>
      <PageHeader
        title="Users"
        subtitle="Invite-only. No passwords — everyone signs in with a magic link."
        actions={
          <button onClick={() => { setForm(EMPTY); setShowInvite(true); setError(""); }}
            data-testid="users-invite-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <UserPlus size={14} weight="bold" /> Invite user
          </button>
        }
      />
      <div className="p-6 space-y-4" data-testid="users-page">
        {toast && (
          <div className="text-xs px-3 py-2 border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan rounded"
            data-testid="users-toast">{toast}</div>
        )}
        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : users.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <UsersIcon size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No users yet</h3>
            <p className="text-sm text-slate-400">Invite your first teammate to get started.</p>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">User</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Created</th>
                  <th className="text-right py-3 px-4 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-border hover:bg-white/5"
                    data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {u.role === "admin"
                          ? <ShieldCheck size={16} weight="duotone" className="text-brand-cyan" />
                          : <UserIcon size={16} weight="duotone" className="text-slate-400" />}
                        <div className="min-w-0">
                          <div className="text-white truncate" data-testid={`user-name-${u.id}`}>{u.name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-300">
                        {u.role}
                        {u.role === "client_poc" && u.client_id && (
                          <span className="text-slate-500 ml-1">· {clientName(u.client_id)}</span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300">
                          <CheckCircle size={11} weight="fill" /> ACTIVE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-300">
                          <Hourglass size={11} weight="bold" /> PENDING
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {!u.is_active && (
                        <button onClick={() => resend(u)} data-testid={`user-resend-${u.id}`}
                          title="Resend invite link"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded mr-1 text-slate-200 transition-colors">
                          <PaperPlaneTilt size={11} weight="bold" /> Resend
                        </button>
                      )}
                      {me?.id !== u.id && (
                        <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}
                          title="Delete user"
                          className="p-1.5 text-slate-300 hover:text-red-400">
                          <Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6"
          onClick={() => setShowInvite(false)}>
          <form onSubmit={invite} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-md p-6 space-y-4" data-testid="invite-form">
            <h3 className="font-display text-lg font-bold tracking-tight">Invite user</h3>
            <p className="text-xs text-slate-400">
              We&apos;ll email them a magic activation link valid for 7 days. They never set a password.
            </p>

            <Field label="Full name" required>
              <input required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="invite-name-input" placeholder="Jane Smith" />
            </Field>
            <Field label="Work email" required>
              <input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="invite-email-input" placeholder="jane@sentryit.com" />
            </Field>
            <Field label="Role">
              <select value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="invite-role-select">
                {ROLES.map(r => <option key={r.v} value={r.v}>{r.l} — {r.desc}</option>)}
              </select>
            </Field>
            {form.role === "client_poc" && (
              <Field label="Scoped client" required>
                <select required value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  data-testid="invite-client-select">
                  <option value="">Pick a client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            )}

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2"
                data-testid="invite-error">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setShowInvite(false)}
                data-testid="invite-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" disabled={submitting}
                data-testid="invite-submit-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold disabled:opacity-60">
                {submitting ? "Sending…" : "Send invite"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded-md px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
